// components/ChatPanel.js
import React, { useState, useRef, useEffect, useMemo } from "react";
import { chatWithAI } from "../api/ChatAI";
import ReactMarkdown from "react-markdown";
import AWS from "aws-sdk";

import { loadOntology } from "../api/OntologyUtils";
import {
  createEntityManager,
  resolveVarsFromContext,
} from "../api/EntityManager.js";

import RoomPickerMessage from "./RoomPickerMessage";
import RoomCard from "./RoomCard";
import { listRoomsInProject, getRoomById } from "../api/ProjectOntologyManager";

import EnvelopeContent from "../components/EnvelopeContent";

import "./ChatPanel.css";
import { isChatEnvelope } from "./EnvelopeContent";

import ChatModal from "../components/ChatModal";
import BaguaCameraPanel from "../components/commands/BaguaCameraPanel";

import { saveBaguaContextToProject } from "../GeoVisionAI";

/* ----------------------------------------------------------------------------
 * ChatPanel
 * ------------------------------------------------------------------------- */

export default function ChatPanel({
  project,
  room,
  sessionId = 0,
  s3Prefix,
  onRoomPicked,
  /** When provided, render this as an AI response and skip backend work */
  mockEnvelope,
}) {
  // ---- Mock mode detection --------------------------------------------------
  const normalizedMockEnv = Array.isArray(mockEnvelope)
    ? mockEnvelope[0]
    : mockEnvelope;
  const mockMode = !!normalizedMockEnv;

  // --------------------------------------------------------------------------
  // Existing state & behavior
  // --------------------------------------------------------------------------
  const propRoomId = room?.id || null;
  const propRoomTitle = room?.title || "";
  const [pickedRoom, setPickedRoom] = useState(null);

  const activeRoomId = pickedRoom?.id ?? propRoomId ?? null;
  const activeRoomTitle = pickedRoom?.title ?? propRoomTitle ?? "";

  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);

  const lastCardKeyRef = useRef(null);

  const inputRef = useRef(null);

  const [attachments, setAttachments] = useState([]); // [{id, type, url, title, source}]

  const [chatSessionState, setChatSessionState] = useState({
    vars: null,
    key: null,
    entity: null,
    created: false,
    notFound: false,
    error: null,
  });

  const chatBodyRef = useRef(null);
  const formRef = useRef(null);

  // near other useState hooks
  const [baguaOpen, setBaguaOpen] = React.useState(false);
  const [baguaReset, setBaguaReset] = React.useState(0); // bump to force remount/reset

  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [messages]);

  async function ensureAwsCreds() {
    const creds = AWS.config?.credentials;
    if (!creds) return null;
    if (typeof creds.get === "function") {
      await new Promise((resolve, reject) =>
        creds.get((err) => (err ? reject(err) : resolve())),
      );
    }
    return creds;
  }

  // Prefill the textbox with image + “Visualize … in room”
  const handleVisualizeFromCard = (card) => {
    const title = card?.title || card?.name || card?.ref || "this item";
    const url =
      card?.link ||
      card?.thumbnail ||
      card?.image_url ||
      card?.imageUrl ||
      card?.image ||
      "";

    const isHttpUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u);

    // Always put a natural-language prompt in the textbox
    setInput(`Visualize "${title}" in room`);

    if (!isHttpUrl(url)) {
      // No image URL; just focus the input and return
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }

    setAttachments((prev) => {
      // de-dupe by URL
      if (prev.some((a) => a.url === url)) return prev;
      const id =
        (globalThis.crypto && crypto.randomUUID && crypto.randomUUID()) ||
        `att-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return [...prev, { id, type: "image-url", url, title, source: card }];
    });

    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // Reset when parent-provided room changes
  useEffect(() => {
    if (mockMode) return; // mock mode: don't reset based on room props
    setMessages([]);
    setPickedRoom(null);
  }, [propRoomId, mockMode]);

  // In mock mode, create a single message holding the envelope
  useEffect(() => {
    if (!mockMode) return;
    setMessages([{ sender: "ai", _envelope: normalizedMockEnv }]);
  }, [mockMode, normalizedMockEnv]);

  // Ensure/Load ChatSession (skip entirely in mock mode)
  useEffect(() => {
    if (mockMode) return;

    let cancelled = false;
    (async () => {
      try {
        const awsCreds = await ensureAwsCreds();
        const identityId = AWS.config?.credentials?.identityId || null;
        if (!identityId) {
          setChatSessionState((s) => ({ ...s, error: "no-identity" }));
          return;
        }
        setUserId(identityId);

        const ontologyObj = await loadOntology("session");
        const mgr = await createEntityManager(ontologyObj);
        if (!mgr?.meta) {
          setChatSessionState((s) => ({ ...s, error: "no-manager" }));
          return;
        }

        const vars = resolveVarsFromContext(mgr.meta, {
          userId: identityId,
          sessionId: sessionId ?? 0,
          s3Prefix,
          roomId: activeRoomId,
        });

        const key = mgr.keyFor({ vars });
        if (cancelled) return;

        try {
          const {
            created,
            key: ensuredKey,
            entity,
          } = await mgr.ensure({
            vars,
            awsCreds,
            init: {
              id: "chat::0",
              scope: "user",
              message_count: 0,
              room_title: activeRoomTitle || null,
            },
          });
          if (cancelled) return;
          setChatSessionState({
            vars,
            key: ensuredKey || key,
            entity,
            created: !!created,
            notFound: false,
            error: null,
          });
        } catch (e) {
          const isNotFound =
            (e && (e.code === "NoSuchKey" || e.name === "NotFoundError")) ||
            /not found|doesn.?t exist|no such key/i.test(
              String(e?.message || ""),
            );
          if (isNotFound) {
            setChatSessionState({
              vars,
              key,
              entity: null,
              created: false,
              notFound: true,
              error: null,
            });
          } else {
            setChatSessionState({
              vars,
              key,
              entity: null,
              created: false,
              notFound: false,
              error: e?.message || "unknown",
            });
          }
        }
      } catch (err) {
        setChatSessionState((s) => ({
          ...s,
          error: err?.message || "unexpected",
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, s3Prefix, activeRoomId, activeRoomTitle, mockMode]);

  // After ChatSession is ready AND we have an activeRoomId, show Room Card once (skip in mock mode)
  useEffect(() => {
    if (mockMode) return;

    const sessionKey = chatSessionState?.key;
    if (!sessionKey || !activeRoomId || !project?.id) return;
    if (chatSessionState.error) return;

    const cardKey = `roomcard::${sessionKey}::${activeRoomId}`;
    if (lastCardKeyRef.current === cardKey) return;
    lastCardKeyRef.current = cardKey;

    setMessages((prev) => [
      ...prev,
      {
        sender: "ai",
        _isRoomCard: true,
        roomPayload: {
          id: activeRoomId,
          title: activeRoomTitle,
          description: room?.description || "",
          image: room?.image || null,
        },
      },
    ]);
  }, [
    chatSessionState?.key,
    chatSessionState?.error,
    activeRoomId,
    activeRoomTitle,
    project?.id,
    room?.description,
    room?.image,
    mockMode,
  ]);

  // Hydrate Room Card (skip in mock mode)
  useEffect(() => {
    if (mockMode) return;
    if (!activeRoomId || !project?.id || !userId) return;

    let cancelled = false;
    (async () => {
      try {
        const awsCreds = await ensureAwsCreds();
        const rooms = await listRoomsInProject({
          userId,
          projectUuid: project.id,
          awsCreds,
        });
        if (cancelled) return;

        const found = Array.isArray(rooms)
          ? rooms.find((r) => r.id === activeRoomId)
          : null;

        if (found) {
          setMessages((prev) =>
            prev.map((m) =>
              m._isRoomCard && m.roomPayload?.id === activeRoomId
                ? {
                    ...m,
                    roomPayload: {
                      ...m.roomPayload,
                      title: found.title ?? m.roomPayload.title,
                      description:
                        found.description ?? m.roomPayload.description,
                      image: found.image ?? m.roomPayload.image,
                    },
                  }
                : m,
            ),
          );
        }
      } catch (e) {
        console.warn("[ChatPanel] room details fetch failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeRoomId, project?.id, userId, mockMode]);

  const onBaguaCamera = () => {
    // Open the modal directly, no chat message, no backend
    setBaguaOpen(true);
  };

  const onDimensions = () => {
    handleSend("Get dimensions of the current room", []);
  };

  const sessionMeta = useMemo(() => {
    const { vars, key, entity, notFound } = chatSessionState || {};
    return {
      entityName: "ChatSession",
      key,
      vars,
      notFound,
      entityId: entity?.id || null,
    };
  }, [chatSessionState]);

  function handleSlashCore(userMessage, { log = console.info } = {}) {
    const msg = String(userMessage || "").trim();
    if (!msg.startsWith("/")) return { handled: false };

    // basic parse: "/bagua-camera foo bar"
    const [_, cmd, ...args] = msg.match(/^\/(\S+)(?:\s+(.*))?$/) || [];
    log(
      `[Slash] command="${cmd || ""}" args="${args?.join?.(" ") || ""}"`.trim(),
    );
    return {
      handled: true,
      cmd: cmd || "",
      args: args && args.length ? args : [],
    };
  }

  // allow optional override text/attachments
  const handleSend = async (
    overrideText = null,
    overrideAttachments = null,
  ) => {
    // Use overrides if provided
    const rawText = overrideText ?? input;
    const userMessage = (rawText || "").trim();
    const outgoingAttachments = Array.isArray(overrideAttachments)
      ? overrideAttachments
      : attachments;

    // Allow sending if there's text OR attachments
    if (!userMessage && outgoingAttachments.length === 0) return;

    // Render the user's message (with attachments) immediately
    setMessages((prev) => [
      ...prev,
      { sender: "user", text: userMessage, attachments: outgoingAttachments },
    ]);

    // Clear composer and show "thinking…"
    setInput("");
    setAttachments([]); // clear staged attachments from the composer

    // Only non-slash messages continue to backend
    setLoading(true);

    const creds = AWS.config?.credentials;
    if (creds && typeof creds.get === "function") {
      try {
        await new Promise((resolve, reject) =>
          creds.get((err) => (err ? reject(err) : resolve())),
        );
      } catch (e) {
        console.error("[ChatPanel] Failed to refresh AWS credentials:", e);
      }
    }

    try {
      setMessages((prev) => [...prev, { sender: "ai", text: "🤔 thinking…" }]);

      // 🔗 Include attachments in the payload to your backend
      const message_body = {
        cognito_id: userId,
        session_id: String(sessionId ?? "0"),
        project_id: project?.id,
        room_id: activeRoomId,
        message: userMessage,
        attachments: outgoingAttachments.map(
          ({ id, type, url, title, source }) => ({
            id,
            type, // e.g., "image-url"
            url, // remote image URL
            title: title ?? "", // optional
            source: source ?? null, // original card or minimal provenance
          }),
        ),
      };

      const response = await chatWithAI(
        JSON.stringify(message_body),
        AWS.config?.credentials,
        { session: sessionMeta },
      );

      setMessages((prev) => {
        const copy = [...prev];

        if (isChatEnvelope(response)) {
          copy[copy.length - 1] = { sender: "ai", _envelope: response };
        } else if (response?.render?.markdown || response?.render?.cards) {
          copy[copy.length - 1] = {
            sender: "ai",
            _envelope: {
              version: "uce/1",
              id: response?.id || "local",
              role: "assistant",
              timestamp: response?.timestamp || new Date().toISOString(),
              render: {
                markdown: response?.render?.markdown || "",
                cards: Array.isArray(response?.render?.cards)
                  ? response.render.cards
                  : [],
              },
            },
          };
        } else {
          const fullText =
            (typeof response === "string" && response) ||
            response?.aiResponse ||
            (typeof response === "object"
              ? "```\n" + JSON.stringify(response, null, 2) + "\n```"
              : String(response ?? ""));
          copy[copy.length - 1] = { sender: "ai", text: fullText };
        }
        return copy;
      });
    } catch (err) {
      console.error(err);
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          sender: "ai",
          text: "Error: Unable to respond.",
        };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePickRoom = async (r) => {
    if (mockMode) return; // no-op in mock mode
    console.log("handlePickRoom", r);

    setPickedRoom(r);
    setMessages((prev) => [
      ...prev,
      { sender: "ai", text: `Great—let’s work in **${r.title || r.id}**.` },
    ]);
    onRoomPicked?.(r);

    try {
      const awsCreds = await ensureAwsCreds();
      const identityId = userId || AWS.config?.credentials?.identityId || null;
      if (!identityId || !project?.id) {
        console.warn(
          "[ChatPanel] Missing identityId or project.id; skipping getRoomById",
          { identityId, projectId: project?.id },
        );
        return;
      }
      if (!userId) setUserId(identityId);

      const fullRoom = await getRoomById({
        userId: identityId,
        projectUuid: project.id,
        roomId: r.id,
        awsCreds,
      });

      console.log("Picked Room", fullRoom);

      if (fullRoom) {
        setPickedRoom(fullRoom);
        onRoomPicked?.(fullRoom);

        setMessages((prev) =>
          prev.map((m) =>
            m._isRoomCard && m.roomPayload?.id === r.id
              ? {
                  ...m,
                  roomPayload: {
                    ...m.roomPayload,
                    title: fullRoom.title ?? m.roomPayload.title,
                    description:
                      fullRoom.description ?? m.roomPayload.description,
                    image: fullRoom.image ?? m.roomPayload.image,
                  },
                }
              : m,
          ),
        );
      } else {
        console.warn("[ChatPanel] getRoomById returned null for", r.id);
      }
    } catch (e) {
      console.warn("[ChatPanel] handlePickRoom failed to load room:", e);
    }
  };

  return (
    <div className="chat-panel-container" aria-label="ChatPanel">
      <h3 className="chat-panel-title">
        {mockMode
          ? normalizedMockEnv?.summary ||
            normalizedMockEnv?.sentiment ||
            "Preview"
          : activeRoomTitle}
      </h3>

      <div
        className="chat-body"
        ref={chatBodyRef}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          overflowY: "auto",
          padding: "1rem",
        }}
      >
        {/* In mock mode, suppress the room picker entirely */}
        {!mockMode && !activeRoomId && project?.id && (
          <RoomPickerMessage projectId={project.id} onPicked={handlePickRoom} />
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`chat-message ${msg.sender}`}>
            {msg._isRoomCard ? (
              <RoomCard
                room={msg.roomPayload}
                projectId={project?.id}
                userId={userId}
                s3Prefix={s3Prefix}
                onLocalImageChange={(dataUrl) => {
                  setMessages((prev) => {
                    const copy = [...prev];
                    const m = copy[idx];
                    if (m && m._isRoomCard && m.roomPayload) {
                      m.roomPayload = { ...m.roomPayload, image: dataUrl };
                    }
                    return copy;
                  });
                }}
                onSaved={() => {}}
              />
            ) : msg._envelope ? (
              <EnvelopeContent
                envelope={msg._envelope}
                onVisualize={handleVisualizeFromCard}
              />
            ) : (
              <>
                <strong>{msg.sender === "user" ? "You" : "AI"}:</strong>{" "}
                {msg.sender === "ai" ? (
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                ) : (
                  msg.text
                )}
              </>
            )}
          </div>
        ))}

        {/* Input is disabled in mock mode (demo/preview only) */}
        <form
          ref={formRef}
          className="chat-panel-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
        >
          <div className="chat-input-wrapper">
            {attachments.length > 0 && (
              <div
                className="chat-attachments"
                role="list"
                aria-label="Pending attachments"
              >
                {attachments.map((att) => (
                  <div className="attachment-card" role="listitem" key={att.id}>
                    <div className="thumb">
                      <img
                        src={att.url}
                        alt={att.title}
                        loading="lazy"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          // graceful placeholder if image 404s
                          e.currentTarget.replaceWith(
                            Object.assign(document.createElement("div"), {
                              className: "thumb thumb--placeholder",
                              textContent: "🖼️",
                            }),
                          );
                        }}
                      />
                    </div>
                    <div className="meta">
                      <div className="title" title={att.title}>
                        {att.title}
                      </div>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() =>
                          setAttachments((list) =>
                            list.filter((x) => x.id !== att.id),
                          )
                        }
                        aria-label={`Remove ${att.title}`}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div
              className="chat-actions-toolbar"
              role="toolbar"
              aria-label="Chat actions"
            >
              <button
                type="button"
                className="icon-btn"
                onClick={onBaguaCamera}
                title="Bagua Camera"
                aria-label="Bagua Camera"
                disabled={mockMode || loading || !activeRoomId}
              >
                {/* Bagua Camera (camera + octagon) */}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M7 7l2-2h6l2 2h2a3 3 0 013 3v6a3 3 0 01-3 3H5a3 3 0 01-3-3V10a3 3 0 013-3h2z"
                    fill="currentColor"
                    opacity=".85"
                  />
                  <circle
                    cx="12"
                    cy="13"
                    r="4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M12 2l3 1 2 3-1 3-3 2-3-1-2-3 1-3 3-2z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    opacity=".8"
                  />
                </svg>
              </button>

              <button
                type="button"
                className="icon-btn"
                onClick={onDimensions}
                title="Dimensions"
                aria-label="Dimensions"
                disabled={mockMode || loading || !activeRoomId}
              >
                {/* Dimensions (isometric cube) */}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2l8 4.5v11L12 22 4 17.5v-11L12 2z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M12 2v20M4 6.5l8 4.5 8-4.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                  />
                </svg>
              </button>
            </div>

            <textarea
              ref={inputRef}
              className="chat-textarea"
              rows="2"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                mockMode
                  ? "Preview mode (input disabled)"
                  : activeRoomId
                    ? "Ask a question..."
                    : project?.id
                      ? "Pick a room to start"
                      : "Select a project to start"
              }
              disabled={mockMode || loading || !activeRoomId}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
          </div>
        </form>
      </div>
      {baguaOpen && (
        <ChatModal
          key={`bagua-${baguaReset}`} // forces full unmount/mount on close for a clean reset
          title="Bagua Camera"
          onClose={() => {
            setBaguaOpen(false);
            setBaguaReset((n) => n + 1); // reset internal state on close
          }}
        >
          <BaguaCameraPanel
            projectId={project?.id}
            onSubmit={({ context } = {}) => {
              // <= default to {}
              console.log("[ChatPanel] Bagua context:", context);
              try {
                console.table?.(context);
                saveBaguaContextToProject({
                  projectId: project?.id,
                  roomId: activeRoomId,
                  contextMap: context,
                  awsCreds: AWS.config.credentials,
                });
                console.log("[ChatPanel] Bagua context saved.");
              } catch (e) {
                console.error("[ChatPanel] Failed to save Bagua context:", e);
                // if you used optimistic UI, consider rollback here
              } finally {
                setBaguaOpen(false);
                setBaguaReset((n) => n + 1);
              }
            }}
          />
        </ChatModal>
      )}
    </div>
  );
}
